import React, { useState, useEffect } from 'react';
import {
  Table,
  Button,
  Modal,
  Form,
  Input,
  Switch,
  message,
  Space,
  Popconfirm,
  Tag,
  Card,
  Pagination,
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  ReloadOutlined,
} from '@ant-design/icons';
import { apiClient } from '../../../config/apiClient';
import MainLayout from '../../layout/layout';
import { usePermissions } from '../../../context/permissionsContext';

const FormasPago = () => {
  const { hasPermission } = usePermissions();
  const [formasPago, setFormasPago] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingFormaPago, setEditingFormaPago] = useState(null);
  const [form] = Form.useForm();
  const mueveCaja = Form.useWatch('mueve_caja', form);
  const [isMobile, setIsMobile] = useState(() => window.innerWidth <= 768);
  const [filtroNombre, setFiltroNombre] = useState('');
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });

  const loadFormasPago = async () => {
    setLoading(true);
    try {
      const response = await apiClient.get('/gestion/formas-pago');
      if (response.ok) {
        const data = await response.json();
        setFormasPago(data);
        setPagination(prev => ({ ...prev, total: data.length }));
      } else {
        message.error('Error al cargar las formas de pago');
      }
    } catch (error) {
      console.error('Error:', error);
      message.error('Error al cargar las formas de pago');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadFormasPago();
  }, []);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const formasPagoFiltradas = formasPago.filter(fp =>
    !filtroNombre || fp.fop_nombre?.toLowerCase().includes(filtroNombre.toLowerCase())
  );

  const formasPagoPaginadas = formasPagoFiltradas.slice(
    (pagination.current - 1) * pagination.pageSize,
    pagination.current * pagination.pageSize
  );

  const handleOpenModal = (formaPago = null) => {
    setEditingFormaPago(formaPago);
    if (formaPago) {
      form.setFieldsValue({
        nombre: formaPago.fop_nombre,
        mueve_caja: formaPago.fop_mueve_caja,
        activo: formaPago.fop_activo,
      });
    } else {
      form.resetFields();
      form.setFieldsValue({ 
        mueve_caja: true,
        activo: true 
      });
    }
    setModalVisible(true);
  };

  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      if (editingFormaPago) {
        const response = await apiClient.put(
          `/gestion/formas-pago/${editingFormaPago.fop_id}`,
          values
        );
        if (response.ok) {
          message.success('Forma de pago actualizada correctamente');
          setModalVisible(false);
          loadFormasPago();
        } else {
          const error = await response.json();
          message.error(error.message || 'Error al actualizar la forma de pago');
        }
      } else {
        const response = await apiClient.post('/gestion/formas-pago', values);
        if (response.ok) {
          message.success('Forma de pago creada correctamente');
          setModalVisible(false);
          loadFormasPago();
        } else {
          const error = await response.json();
          message.error(error.message || 'Error al crear la forma de pago');
        }
      }
    } catch (error) {
      if (error.errorFields) return;
      console.error('Error:', error);
      message.error('Error al guardar la forma de pago');
    }
  };

  const handleDelete = async (id) => {
    try {
      const response = await apiClient.delete(`/gestion/formas-pago/${id}`);
      if (response.ok) {
        message.success('Forma de pago eliminada correctamente');
        loadFormasPago();
      } else {
        const error = await response.json();
        message.error(error.message || 'Error al eliminar la forma de pago');
      }
    } catch (error) {
      console.error('Error:', error);
      message.error('Error al eliminar la forma de pago');
    }
  };

  const columns = [
    {
      title: 'Nombre',
      dataIndex: 'fop_nombre',
      key: 'fop_nombre',
    },
    {
      title: 'Mueve Caja',
      dataIndex: 'fop_mueve_caja',
      key: 'fop_mueve_caja',
      width: 120,
      align: 'center',
      render: (mueveCaja) => (
        <Tag color={mueveCaja ? 'blue' : 'default'}>
          {mueveCaja ? 'Sí' : 'No'}
        </Tag>
      ),
    },
    {
      title: 'Estado',
      dataIndex: 'fop_activo',
      key: 'fop_activo',
      width: 100,
      align: 'center',
      render: (activo) => (
        <Tag color={activo ? 'success' : 'default'}>
          {activo ? 'Activo' : 'Inactivo'}
        </Tag>
      ),
    },
    ...((hasPermission('formas_pago', 'editar') || hasPermission('formas_pago', 'eliminar')) ? [{
      title: 'Acciones',
      key: 'acciones',
      width: 150,
      align: 'center',
      render: (_, record) => (
        <Space>
          {hasPermission('formas_pago', 'editar') && (
            <Button
              type="link"
              icon={<EditOutlined />}
              onClick={() => handleOpenModal(record)}
              size="small"
            >
              Editar
            </Button>
          )}
          {hasPermission('formas_pago', 'eliminar') && (
            <Popconfirm
              title="¿Estás seguro de eliminar esta forma de pago?"
              onConfirm={() => handleDelete(record.fop_id)}
              okText="Sí"
              cancelText="No"
            >
              <Button type="link" danger icon={<DeleteOutlined />} size="small">
                Eliminar
              </Button>
            </Popconfirm>
          )}
        </Space>
      ),
    }] : []),
  ];

  return (
    <MainLayout>
      <div style={{ padding: '24px' }}>
        <div style={{ marginBottom: 16 }}>
          <h1 style={{ margin: 0, marginBottom: 16, color: '#ffffff', fontSize: isMobile ? '20px' : '24px' }}>
            Formas de Pago
          </h1>
          <Space wrap style={{ marginBottom: 16 }}>
            <Button
              icon={<ReloadOutlined />}
              onClick={loadFormasPago}
              loading={loading}
            >
              {isMobile ? null : 'Actualizar'}
            </Button>
            {hasPermission('formas_pago', 'crear') && (
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={() => handleOpenModal(null)}
              >
                Nueva Forma de Pago
              </Button>
            )}
          </Space>

          {/* Filtro */}
          <Card style={{ marginBottom: 16, backgroundColor: '#2d2d2d', border: 'none' }}>
            <Input
              placeholder="Buscar por nombre..."
              value={filtroNombre}
              onChange={(e) => {
                setFiltroNombre(e.target.value);
                setPagination(prev => ({ ...prev, current: 1 }));
              }}
              allowClear
              style={{ maxWidth: 300 }}
            />
          </Card>
        </div>

        <Table
          columns={columns}
          dataSource={formasPagoPaginadas}
          loading={loading}
          rowKey="fop_id"
          pagination={{
            current: pagination.current,
            pageSize: pagination.pageSize,
            total: formasPagoFiltradas.length,
            showSizeChanger: true,
            showTotal: (total) => `Total: ${total} formas de pago`,
            onChange: (page, pageSize) => {
              setPagination(prev => ({ ...prev, current: page, pageSize }));
            },
          }}
        />

        {/* Modal para crear/editar */}
        <Modal
          title={editingFormaPago ? 'Editar Forma de Pago' : 'Nueva Forma de Pago'}
          open={modalVisible}
          onOk={handleSave}
          onCancel={() => {
            setModalVisible(false);
            form.resetFields();
          }}
          okText="Guardar"
          cancelText="Cancelar"
          width={500}
        >
          <Form form={form} layout="vertical">
            <Form.Item
              name="nombre"
              label="Nombre"
              rules={[{ required: true, message: 'Ingrese el nombre de la forma de pago' }]}
            >
              <Input placeholder="Ej: Efectivo, Transferencia, QR" />
            </Form.Item>

            <Form.Item
              name="mueve_caja"
              label="Mueve Caja"
              valuePropName="checked"
            >
              <Switch checkedChildren="Sí" unCheckedChildren="No" />
            </Form.Item>
            <div style={{ marginTop: -16, marginBottom: 16, paddingLeft: 4 }}>
              <span style={{ color: '#b0b0b0', fontSize: '12px' }}>
                {mueveCaja !== false ? 'Impacta en la caja diaria' : 'No impacta en la caja diaria'}
              </span>
            </div>

            <Form.Item
              name="activo"
              label="Activo"
              valuePropName="checked"
            >
              <Switch checkedChildren="Activo" unCheckedChildren="Inactivo" />
            </Form.Item>
          </Form>
        </Modal>
      </div>
    </MainLayout>
  );
};

export default FormasPago;
